import { delay } from '@holmescorp/common';

import { userstore } from '../base';
import {
	loadUserstate,
	statusChange,
	examdateUpdateCount,
	assessmentChange,
	examdateChange,
	locationChange,
	posttestChange,
	practiceChange,
	quizPatternChange,
	toolstateChange,
	mpeInit
} from '../UserState';
import { svc } from './statesvc';
import { serverConfigChange, tapaneChange } from '../LccStore';
import { setPrevNext } from './rollup-status';
import { tgfkeys, childmap } from './loadroots';
import { ParentTree, PostTestResults } from '@holmescorp/lcc-interfaces';
import { get_state, get_user_state, set_user_state } from 'cache-store';
import { ToolState, ServerConfig, MPEItems } from '../interfaces';

export const tgfcomplete = new Set();

const assessment_getstate = ({ key }: { key: 'assessment' }) => svc.get_state({ key });
const assessment_statechange = (assessment: ParentTree) => assessmentChange({ assessment });
const examdate_getstate = ({ key }: { key: 'examdate' }) => svc.get_state({ key });
const examdate_statechange = (examdate: string) => examdateChange({ examdate });
const location_getstate = ({ key }: { key: 'location' }) => svc.get_state({ key });
const location_statechange = (location: string) => locationChange({ location });
const posttest_getstate = ({ key }: { key: 'posttest' }) => svc.get_state({ key });
const posttest_statechange = (posttest: PostTestResults) => posttestChange({ posttest });
const practice_getstate = ({ key }: { key: 'practice' }) => svc.get_state({ key });
const practice_statechange = (practice: ParentTree) => practiceChange({ practice });
const quizpattern_getstate = ({ key }: { key: 'quizpattern' }) => svc.get_state({ key });
const quizpattern_statechange = (quizPattern: string) => quizPatternChange({ quizPattern });
const status_getstate = ({ key }: { key: 'status' }) => svc.get_state({ key });
const status_statechange = (status: ParentTree) => statusChange({ status });
const toolstate_getstate = ({ key }: { key: 'toolstate' }) => svc.get_state({ key });
const toolstate_statechange = (toolstate: ToolState) => toolstateChange({ toolstate });

const mpe_getstate = ({ key }: { key: 'mpe' }) => svc.get_state({ key });
const mpe_statechange = (mpe: MPEItems) => mpeInit({ items: mpe });

function setAccordionKeys(status: ParentTree): void {
	const active = new Set(Array.from(tgfkeys));
	tgfkeys.forEach((tgfkey) => {
		const current = status[tgfkey];
		// if all topics are marked read
		if (current.data.complete === current.data.total) {
			const tgroups = childmap.get(tgfkey) || [];
			let passing = tgroups.length > 0;
			// if all quizzes are taken & passing
			tgroups.forEach((tgroup) => {
				const tgroupstatus = status[tgroup];
				passing =
					passing &&
					tgroupstatus.data.percent !== undefined &&
					tgroupstatus.data.percent !== null &&
					tgroupstatus.data.percent >= 0.8;
			});
			if (passing) {
				active.delete(tgfkey);
				tgfcomplete.add(tgfkey);
			}
		}
	});
	tapaneChange({ accordionSelected: Array.from(active) });
}

class QuizSwitch {
	private _switching: boolean;

	constructor() {
		this._switching = false;
	}

	public get switching(): boolean {
		return this._switching;
	}

	public async get_status() {
		this._switching = true;
		const status = await svc.get_state({ key: 'status' });
		statusChange({ status });
		const assessment = await svc.get_state({ key: 'assessment' });
		assessmentChange({ assessment });
		const practice = await svc.get_state({ key: 'practice' });
		practiceChange({ practice });
		const posttest = await svc.get_state({ key: 'posttest' });
		posttestChange({ posttest });
		await delay(1000);
		this._switching = false;
	}
}

const quizSwitch = new QuizSwitch();

export function track_userstate(): void {
	userstore.onChange(userstore.path('quizPattern'), () => {
		const { get, path } = userstore;
		const state = get(path('quizPattern'));
		set_user_state({
			key: 'quizpattern',
			state,
			setstate: ({ key, state }: { key: 'quizpattern'; state: string }) => svc.set_state({ key, state })
		}).then(() => {
			quizSwitch.get_status();
		});
	});

	userstore.onChange(userstore.path('examdate'), () => {
		const { get, path } = userstore;
		const state = get(path('examdate'));
		set_user_state({
			key: 'examdate',
			state,
			setstate: ({ key, state }: { key: 'examdate'; state: string }) => svc.set_state({ key, state })
		});
	});

	userstore.onChange(userstore.path('status'), () => {
		if (quizSwitch.switching) {
			return;
		}
		const { get, path } = userstore;
		const state = get(path('status'));
		set_user_state({
			key: 'status',
			state,
			setstate: ({ key, state }: { key: 'status'; state: ParentTree }) => svc.set_state({ key, state })
		});
		// svc.set_state({ key: 'status', state });
	});

	userstore.onChange(userstore.path('toolstate'), () => {
		const { get, path } = userstore;
		const state = get(path('toolstate'));
		set_user_state({
			key: 'toolstate',
			state,
			setstate: ({ key, state }: { key: 'toolstate'; state: ToolState }) => svc.set_state({ key, state })
		});
	});

	userstore.onChange(userstore.path('location'), () => {
		const { get, path } = userstore;
		const state = get(path('location'));
		svc.set_state({ key: 'location', state });
		set_user_state({
			key: 'location',
			state,
			setstate: ({ key, state }: { key: 'location'; state: string }) => svc.set_state({ key, state })
		});
	});

	userstore.onChange(userstore.path('assessment'), () => {
		const { get, path } = userstore;
		const state = get(path('assessment'));
		set_user_state({
			key: 'assessment',
			state,
			setstate: ({ key, state }: { key: 'assessment'; state: ParentTree }) => svc.set_state({ key, state })
		});
	});

	userstore.onChange(userstore.path('posttest'), () => {
		const { get, path } = userstore;
		const state = get(path('posttest'));
		set_user_state({
			key: 'posttest',
			state,
			setstate: ({ key, state }: { key: 'posttest'; state: PostTestResults }) => svc.set_state({ key, state })
		});
	});

	userstore.onChange(userstore.path('practice'), () => {
		const { get, path } = userstore;
		const state = get(path('practice'));
		set_user_state({
			key: 'practice',
			state,
			setstate: ({ key, state }: { key: 'practice'; state: ParentTree }) => svc.set_state({ key, state })
		});
	});

	userstore.onChange(userstore.path('mpe'), () => {
		const { get, path } = userstore;
		const state = get(path('mpe'));
		set_user_state({
			key: 'mpe',
			state,
			setstate: ({ key, state }: { key: 'mpe'; state: MPEItems }) => svc.set_state({ key, state })
		});
	});

	const { get, path } = userstore;
	setAccordionKeys(get(path('status')));
}

export async function load_siteconfig(): Promise<ServerConfig> {
	const config = await get_state({
		svc_call: () => svc.get_config(),
		key: 'siteconfig'
	});
	serverConfigChange({ config });
	return config;
}

export async function load_userstate(): Promise<boolean> {
	const location = await get_user_state({
		key: 'location',
		getstate: location_getstate,
		statechange: location_statechange
	});
	await delay(250);
	const assessment = await get_user_state({
		key: 'assessment',
		getstate: assessment_getstate,
		statechange: assessment_statechange
	});
	const quizPattern = await get_user_state({
		key: 'quizpattern',
		getstate: quizpattern_getstate,
		statechange: quizpattern_statechange
	});
	const status = await get_user_state({
		key: 'status',
		getstate: status_getstate,
		statechange: status_statechange
	});

	const [examdate, posttest, practice, toolstate, mpe] = await Promise.all([
		get_user_state({ key: 'examdate', getstate: examdate_getstate, statechange: examdate_statechange }),
		get_user_state({ key: 'posttest', getstate: posttest_getstate, statechange: posttest_statechange }),
		get_user_state({ key: 'practice', getstate: practice_getstate, statechange: practice_statechange }),
		get_user_state({ key: 'toolstate', getstate: toolstate_getstate, statechange: toolstate_statechange }),
		get_user_state({ key: 'mpe', getstate: mpe_getstate, statechange: mpe_statechange })
	]);

	const setstate = {
		assessment,
		quizPattern,
		examdate,
		location,
		posttest,
		practice,
		status,
		toolstate,
		mpe
	};
	loadUserstate(setstate);
	examdateUpdateCount();
	setPrevNext();
	return true;
}
